import {
  Box,
  Typography,
  Card,
  CardContent
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { formatBRL } from "../metrics/utils/formatters";

export default function CompetitorChart({ productData }) {
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <Box sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          p: 2,
          minWidth: 200
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
            {data.is_current ? 'Nosso Produto' : data.fullName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
            Preço: {formatBRL(data.price)}
          </Typography>
          {!data.is_current && data.sales !== undefined && (
            <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
              Vendas: {data.sales} unidades
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  const formatLabel = (label) => {
    return label.length > 15 ? `${label.slice(0, 15)}...` : label;
  };

  const getBarColor = (entry) => {
    if (entry.is_current) return '#ff7043';
    if (entry.price === productData.suggested_price?.amount) return '#22c55e';
    if (entry.price === productData.lowest_price?.amount) return '#8b5cf6';
    return '#64748b';
  };


  // Estrutura baseada na API real
  const competitors = productData.competitors || [];
  
  const chartData = [
    {
      id: 'nosso-produto',
      name: 'Nosso Produto',
      fullName: 'Nosso Produto',
      price: productData.current_price?.amount || 0,
      is_current: true
    },
    ...competitors.map((competitor, index) => {
      const title = competitor.info?.title || `Competidor ${index + 1}`;
      const price = competitor.price?.amount || 0;
      const sales = competitor.info?.sold_quantity || 0;
      
      return {
        id: `competitor-${index}`,
        name: `${title.substring(0, 10)}... ${price}`,
        fullName: title,
        price: price,
        sales: sales,
        is_current: false
      };
    })
  ].sort((a, b) => a.price - b.price);


  if (!competitors || competitors.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 8,
        color: '#64748b' 
      }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sem dados de competidores
        </Typography>
        <Typography variant="body2">
          Não há informações de competidores disponíveis para este produto
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{
      boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)',
      borderRadius: '16px',
      border: '1px solid rgba(0,0,0,0.06)'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#1a1a1a',
            mb: 3
          }}
        >
          Comparação de Preços
        </Typography>
        
        <Box sx={{ width: '100%', height: 400 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#64748b' }}
                angle={-45}
                textAnchor="end"
                height={80}
                tickFormatter={formatLabel}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => formatBRL(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="price" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          mt: 3,
          justifyContent: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              backgroundColor: '#ff7043',
              borderRadius: '2px' 
            }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b' }}>
              Nosso Preço
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              backgroundColor: '#22c55e',
              borderRadius: '2px' 
            }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b' }}>
              Preço Sugerido
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              backgroundColor: '#8b5cf6',
              borderRadius: '2px' 
            }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b' }}>
              Menor Preço
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              backgroundColor: '#64748b',
              borderRadius: '2px' 
            }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b' }}>
              Competidores
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}